import React, { useState, useEffect, useReducer, useContext, useCallback, useMemo } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import SubscriptionModal from "../../components/SubscriptionModal";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import ChangePlanDialog from "../../components/ChangePlanDialog";
import PlanAddonsCard from "../../components/PlanAddonsCard";
import TemplateCreditCard from "../../components/TemplateCreditCard";
import AiCreditCard from "../../components/AiCreditCard";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { translateStatusDetail } from "../../components/CheckoutPage/CheckoutPage";
import { i18n } from "../../translate/i18n";

import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload;
    const newUsers = [];

    invoices.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  heroCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  statusLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  ctaButton: {
    padding: theme.spacing(1.2, 4),
    fontSize: "1rem",
    fontWeight: 600,
  },
  sectionTitle: {
    margin: theme.spacing(1, 0),
  },
  accordion: {
    marginBottom: theme.spacing(1),
    "&:before": { display: "none" },
  },
  accordionDetails: {
    flexDirection: "column",
    gap: 8,
  },
}));

const PAYMENT_STATUS_LABEL = {
  approved: "financeiro.paymentStatus.approved",
  pending: "financeiro.paymentStatus.pending",
  rejected: "financeiro.paymentStatus.rejected",
  cancelled: "financeiro.paymentStatus.cancelled",
  refunded: "financeiro.paymentStatus.refunded",
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });

const Invoices = () => {
  const classes = useStyles();
  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);

  const isCompanyAdmin = user?.profile === "admin";

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [invoices, dispatch] = useReducer(reducer, []);
  const [storagePlans, setStoragePlans] = React.useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [modalInitialTab, setModalInitialTab] = useState(undefined);
  const [changePlanOpen, setChangePlanOpen] = useState(false);

  const [subscription, setSubscription] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsHasMore, setPaymentsHasMore] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  // força o PlanAddonsCard a recarregar quando algo muda (socket/refresh)
  const [addonsRefreshKey, setAddonsRefreshKey] = useState(0);
  // Recarrega a lista de faturas no refreshAll. Só voltar a página para 1 não
  // basta: se ela já é 1 o React ignora o set, a busca não roda e a lista fica
  // vazia depois do RESET.
  const [invoicesReloadKey, setInvoicesReloadKey] = useState(0);

  const handleOpenContactModal = (invoice, initialTab) => {
    setStoragePlans(invoice);
    setModalInitialTab(initialTab);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription/status");
      setSubscription(data.subscription);
      setCompanyInfo(data.company);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchPayments = useCallback(async (page = 1) => {
    try {
      const { data } = await api.get("/billing/payments", {
        params: { pageNumber: page },
      });
      setPayments((prev) =>
        page === 1 ? data.payments : [...prev, ...data.payments]
      );
      setPaymentsHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const refreshAll = useCallback(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setInvoicesReloadKey((k) => k + 1);
    setPaymentsPage(1);
    fetchSubscriptionStatus();
    fetchPayments(1);
    setAddonsRefreshKey((k) => k + 1);
  }, [fetchSubscriptionStatus, fetchPayments]);

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchPayments(1);
  }, [fetchSubscriptionStatus, fetchPayments]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchInvoices = async () => {
        try {
          const { data } = await api.get("/invoices/all", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_INVOICES", payload: data });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchInvoices();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, invoicesReloadKey]);

  // sockets: pagamento confirmado / eventos de assinatura
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const onPayment = (data) => {
      if (data.action === "CONCLUIDA") {
        refreshAll();
      }
    };
    const onSubscription = (data) => {
      if (data.action === "charge_failed") {
        toast.error(
          i18n.t("financeiro.toasts.chargeFailed", {
            reason: translateStatusDetail(data.statusDetail),
          })
        );
      }
      refreshAll();
    };

    socket.on(`company-${companyId}-payment`, onPayment);
    socket.on(`company-${companyId}-subscription`, onSubscription);

    return () => {
      socket.off(`company-${companyId}-payment`, onPayment);
      socket.off(`company-${companyId}-subscription`, onSubscription);
    };
  }, [socketManager, refreshAll]);

  const handleCancelSubscription = async () => {
    try {
      await api.delete("/subscription/preapproval");
      toast.success(i18n.t("financeiro.toasts.subscriptionCancelled"));
      refreshAll();
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const rowStyle = (record) => {
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    if (dias < 0 && record.status !== "paid") {
      return { backgroundColor: "#ffbcbc9c" };
    }
  };

  const rowStatus = (record) => {
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    const status = record.status;
    if (status === "paid") {
      return "paid";
    }
    if (dias < 0) {
      return "overdue";
    } else {
      return "open";
    }
  };

  // ===== situação da licença (um olhar, uma ação) =====
  // label/detail guardam [chave, parâmetros]: o texto é traduzido no render
  // para seguir o idioma em uso.
  const licenseInfo = useMemo(() => {
    const dueDateRaw = companyInfo?.dueDate;
    if (!dueDateRaw) {
      return { kind: "unknown", label: ["financeiro.license.unavailable"] };
    }
    const due = moment(String(dueDateRaw).substring(0, 10), "YYYY-MM-DD");
    if (!due.isValid()) {
      return { kind: "unknown", label: ["financeiro.license.unavailable"] };
    }
    if (due.year() > 2037) {
      return { kind: "ok", label: ["financeiro.license.noExpiration"] };
    }
    const days = due.diff(moment().startOf("day"), "days");
    const dateStr = due.format("DD/MM/YYYY");
    if (days < 0) {
      return {
        kind: "overdue",
        label: ["financeiro.license.overdue", { count: Math.abs(days) }],
        detail: ["financeiro.license.overdueDetail", { date: dateStr }],
      };
    }
    if (days === 0) {
      return { kind: "warning", label: ["financeiro.license.dueToday"], detail: ["financeiro.license.dueDate", { date: dateStr }] };
    }
    if (days <= 5) {
      return {
        kind: "warning",
        label: ["financeiro.license.dueIn", { count: days }],
        detail: ["financeiro.license.dueDate", { date: dateStr }],
      };
    }
    return { kind: "ok", label: ["financeiro.license.upToDate"], detail: ["financeiro.license.validUntil", { date: dateStr }] };
  }, [companyInfo]);

  const STATUS_VISUAL = {
    ok: { color: "#4caf50", Icon: CheckCircleIcon },
    warning: { color: "#ff9800", Icon: WarningIcon },
    overdue: { color: "#f44336", Icon: ErrorIcon },
    unknown: { color: "#9e9e9e", Icon: WarningIcon },
  };
  const visual = STATUS_VISUAL[licenseInfo.kind] || STATUS_VISUAL.unknown;
  const StatusIcon = visual.Icon;

  const monthlyValue = companyInfo?.monthlyValue ?? companyInfo?.plan?.value;
  const subscriptionActive = subscription?.status === "active";
  const subscriptionFailed = subscription?.status === "payment_failed";

  // fatura de mensalidade em aberto mais antiga (pró-rata de add-on fica fora)
  const openInvoicesCount = invoices.filter(
    (inv) => inv.status !== "paid"
  ).length;

  return (
    <MainContainer>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={storagePlans}
        initialTab={modalInitialTab}
      ></SubscriptionModal>
      <ChangePlanDialog
        open={changePlanOpen}
        onClose={() => setChangePlanOpen(false)}
        currentPlan={companyInfo?.plan}
        subscriptionActive={subscriptionActive}
        onChanged={refreshAll}
      />
      <ConfirmationModal
        title={i18n.t("financeiro.cancelModal.title")}
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleCancelSubscription}
      >
        {i18n.t("financeiro.cancelModal.message")}
      </ConfirmationModal>
      <MainHeader>
        <Title>{i18n.t("mainDrawer.listItems.financeiro")}</Title>
      </MainHeader>

      <div className={classes.scrollArea}>
        {/* ===== Card principal: situação + UMA ação ===== */}
        <Paper className={classes.heroCard} variant="outlined">
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            gridGap={24}
          >
            <Box flex={1} minWidth={260}>
              <Box className={classes.statusLine}>
                <StatusIcon style={{ color: visual.color }} />
                <Typography variant="h6" style={{ color: visual.color }}>
                  {i18n.t(...licenseInfo.label)}
                </Typography>
              </Box>
              {licenseInfo.detail && (
                <Typography variant="body2" color="textSecondary">
                  {i18n.t(...licenseInfo.detail)}
                </Typography>
              )}
              <Typography variant="body1" style={{ marginTop: 8 }}>
                {companyInfo?.plan?.name
                  ? i18n.t("financeiro.planNamed", { name: companyInfo.plan.name })
                  : i18n.t("financeiro.plan")}
                {" — "}
                <strong>
                  {i18n.t("financeiro.perMonth", {
                    value: formatCurrency(monthlyValue),
                  })}
                </strong>
              </Typography>

              {subscriptionActive && (
                <Box mt={1} display="flex" alignItems="center" gridGap={8} flexWrap="wrap">
                  <Chip
                    size="small"
                    icon={<CheckCircleIcon style={{ color: "#fff" }} />}
                    label={i18n.t("financeiro.autoRenewActive")}
                    style={{ backgroundColor: "#4caf50", color: "#fff" }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    {subscription?.payerEmail
                      ? i18n.t("financeiro.monthlyCardDebitEmail", {
                          email: subscription.payerEmail,
                        })
                      : i18n.t("financeiro.monthlyCardDebit")}
                  </Typography>
                  <Button
                    size="small"
                    style={{ textTransform: "none" }}
                    onClick={() => setCancelModalOpen(true)}
                  >
                    {i18n.t("financeiro.cancelRenewal")}
                  </Button>
                </Box>
              )}
              {subscriptionFailed && (
                <Typography
                  variant="body2"
                  style={{ color: "#f44336", marginTop: 8 }}
                >
                  {i18n.t("financeiro.lastChargeDeclined")}
                </Typography>
              )}
            </Box>

            {/* CTA único */}
            {!subscriptionActive && (
              <Box textAlign="center" minWidth={240}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.ctaButton}
                  onClick={() => handleOpenContactModal({}, 0)}
                >
                  {licenseInfo.kind === "overdue"
                    ? i18n.t("financeiro.regularizeNow")
                    : i18n.t("financeiro.payMonthlyFee")}
                </Button>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  {i18n.t("financeiro.pixOrCard")}
                </Typography>
                <Button
                  size="small"
                  color="primary"
                  style={{ textTransform: "none", marginTop: 4 }}
                  onClick={() => handleOpenContactModal({}, 1)}
                >
                  {i18n.t("financeiro.preferAutoRenew")}
                </Button>
              </Box>
            )}
            {subscriptionActive && subscriptionFailed === false && (
              <Box textAlign="center" minWidth={200}>
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("financeiro.nothingToDo")}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* ===== Créditos de template (só empresas com cobrança habilitada) ===== */}
        <TemplateCreditCard
          refreshKey={addonsRefreshKey}
          onPay={(invoice) => handleOpenContactModal(invoice, 0)}
        />

        {/* ===== Créditos de IA (só empresas pré-pagas; cortesia não vê) ===== */}
        <AiCreditCard
          refreshKey={addonsRefreshKey}
          onPay={(invoice) => handleOpenContactModal(invoice, 0)}
        />

        {/* ===== Gerenciar plano (add-ons e troca) — recolhido ===== */}
        {isCompanyAdmin && (
          <Accordion className={classes.accordion} variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {i18n.t("financeiro.managePlan")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <PlanAddonsCard
                refreshKey={addonsRefreshKey}
                onChanged={refreshAll}
                onPayInvoice={(invoice) => handleOpenContactModal(invoice, 0)}
              />
              {/* Plano sem WhatsApp via QR Code só é trocado pelo super admin */}
              {companyInfo && companyInfo.plan?.useWhatsappQr !== false && (
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setChangePlanOpen(true)}
                  >
                    {i18n.t("financeiro.changePlan")}
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* ===== Faturas e histórico — recolhido ===== */}
        <Accordion className={classes.accordion} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              {openInvoicesCount > 0
                ? i18n.t("financeiro.invoicesAndHistoryOpen", {
                    count: openInvoicesCount,
                  })
                : i18n.t("financeiro.invoicesAndHistory")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              {i18n.t("financeiro.invoices")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Id</TableCell>
                  <TableCell align="center">{i18n.t("financeiro.table.details")}</TableCell>
                  <TableCell align="center">{i18n.t("financeiro.table.value")}</TableCell>
                  <TableCell align="center">{i18n.t("financeiro.table.dueDate")}</TableCell>
                  <TableCell align="center">{i18n.t("common.status")}</TableCell>
                  <TableCell align="center">{i18n.t("financeiro.table.action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {invoices.map((invoice) => (
                    <TableRow style={rowStyle(invoice)} key={invoice.id}>
                      <TableCell align="center">{invoice.id}</TableCell>
                      <TableCell align="center">{invoice.detail}</TableCell>
                      <TableCell style={{ fontWeight: "bold" }} align="center">
                        {formatCurrency(invoice.value)}
                      </TableCell>
                      <TableCell align="center">
                        {moment(invoice.dueDate).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell style={{ fontWeight: "bold" }} align="center">
                        {i18n.t(`financeiro.invoiceStatus.${rowStatus(invoice)}`)}
                      </TableCell>
                      <TableCell align="center">
                        {rowStatus(invoice) !== "paid" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => handleOpenContactModal(invoice)}
                          >
                            {i18n.t("financeiro.pay")}
                          </Button>
                        ) : (
                          <Button size="small" variant="outlined">
                            {i18n.t("financeiro.paid")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={6} />}
                </>
              </TableBody>
            </Table>
            {hasMore && (
              <Box textAlign="center" my={1}>
                <Button size="small" onClick={loadMore}>
                  {i18n.t("financeiro.loadMoreInvoices")}
                </Button>
              </Box>
            )}

            {/* Histórico de pagamentos (inclui tentativas recusadas) */}
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              {i18n.t("financeiro.paymentHistory")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{i18n.t("common.date")}</TableCell>
                  <TableCell align="center">{i18n.t("common.type")}</TableCell>
                  <TableCell align="center">{i18n.t("financeiro.table.method")}</TableCell>
                  <TableCell align="center">{i18n.t("financeiro.table.value")}</TableCell>
                  <TableCell align="center">{i18n.t("common.status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("financeiro.noPayments")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell align="center">
                      {moment(payment.paidAt || payment.createdAt).format(
                        "DD/MM/YYYY HH:mm"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {payment.type === "recurring"
                        ? i18n.t("financeiro.paymentType.recurring")
                        : i18n.t("financeiro.paymentType.oneTime")}
                    </TableCell>
                    <TableCell align="center">
                      {payment.method === "pix"
                        ? "Pix"
                        : i18n.t("financeiro.paymentMethod.creditCard")}
                    </TableCell>
                    <TableCell align="center">
                      {formatCurrency(payment.value)}
                    </TableCell>
                    <TableCell align="center">
                      <span
                        style={{
                          fontWeight: "bold",
                          color:
                            payment.status === "approved"
                              ? "#4caf50"
                              : payment.status === "rejected"
                              ? "#f44336"
                              : undefined,
                        }}
                      >
                        {PAYMENT_STATUS_LABEL[payment.status]
                          ? i18n.t(PAYMENT_STATUS_LABEL[payment.status])
                          : payment.status}
                      </span>
                      {payment.status === "rejected" && payment.statusDetail && (
                        <Typography variant="caption" display="block">
                          {translateStatusDetail(payment.statusDetail)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {paymentsHasMore && (
              <Box textAlign="center" my={1}>
                <Button
                  size="small"
                  onClick={() => {
                    const next = paymentsPage + 1;
                    setPaymentsPage(next);
                    fetchPayments(next);
                  }}
                >
                  {i18n.t("common.loadMore")}
                </Button>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </div>
    </MainContainer>
  );
};

export default Invoices;
